"use client";

import type { BaseIconName } from "@repo/ui/components/icon";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { memo, useMemo } from "react";

import { useAvatarPicker } from "./avatar-picker.context";
import { cleanName, emojiCategories } from "./avatar-picker.helpers";
import type { EmojiCategory } from "./avatar-picker.types";

const CATEGORIES: { icon: BaseIconName; cat: EmojiCategory; label: string }[] =
  [
    { icon: "emoji-grin", cat: "people", label: "People" },
    { icon: "leaf", cat: "nature", label: "Nature" },
    { icon: "burger", cat: "foods", label: "Food" },
    { icon: "trophy", cat: "activity", label: "Activity" },
    { icon: "building", cat: "places", label: "Places" },
    { icon: "bulb", cat: "objects", label: "Objects" },
    { icon: "hashtag", cat: "symbols", label: "Symbols" },
    { icon: "flag", cat: "flags", label: "Flags" },
  ];

const EmojiItem = memo(({ name, native }: { name: string; native: string }) => {
  const { setEmoji } = useAvatarPicker();

  return (
    <Tooltip>
      <TooltipTrigger
        className="flex size-6 cursor-pointer items-center justify-center rounded-sm text-base leading-none hover:bg-muted"
        onClick={() => setEmoji(native)}
      >
        {native}
      </TooltipTrigger>
      <TooltipContent side="bottom">{cleanName(name)}</TooltipContent>
    </Tooltip>
  );
});

export const Emojis = () => {
  const { emojiCategory, setEmojiCategory, emojiSearchResults, isSearching } =
    useAvatarPicker();

  const emojiList = useMemo(
    () => emojiSearchResults ?? emojiCategories[emojiCategory] ?? [],
    [emojiSearchResults, emojiCategory],
  );

  return (
    <>
      {/* Scrollable emoji grid */}
      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div className="grid auto-rows-[24px] grid-cols-12 content-start gap-0 p-2">
          {emojiList.map(({ id, name, skins }: any) => (
            <EmojiItem key={id} name={name} native={skins[0].native} />
          ))}
        </div>
      </div>

      {/* Category tabs pinned to bottom — hidden during search */}
      {!isSearching && (
        <div className="flex shrink-0 items-center justify-between border-t px-2 py-1">
          {CATEGORIES.map(({ icon, cat, label }) => (
            <Tooltip key={cat}>
              <TooltipTrigger
                className={cn(
                  "flex size-6 cursor-pointer items-center justify-center rounded-sm",
                  cat === emojiCategory
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setEmojiCategory(cat)}
              >
                <Icon name={icon} className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent side="top">{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}
    </>
  );
};
